// src/pages/Team.js
// The organization: its name, members and invitations. Everyone sees the members;
// owners and admins rename the organization, invite people, change roles and remove
// members. Only the owner can hand ownership to someone else.
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { orgAPI } from '../utils/api';
import { useStore } from '../store';
import { Avatar } from './Profile';

const errorOf = (err, fallback) => err.response?.data?.error || fallback;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—');

const ROLE_HELP = {
  owner: 'Everything, including deleting the organization and transferring ownership',
  admin: 'Manage the team and connect or remove social accounts',
  member: 'Create, schedule and edit posts, use groups and upload media',
};

function Card({ title, subtitle, children, right }) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div className="card-title">{title}</div>
          {subtitle && <div className="page-subtitle">{subtitle}</div>}
        </div>
        {right}
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function RoleBadge({ role }) {
  const style = {
    owner: { background: '#ede9fe', color: '#5b21b6' },
    admin: { background: '#ccfbf1', color: '#115e59' },
    member: { background: '#f0f1f5', color: '#4a5568' },
  }[role];
  return <span className="badge" style={{ ...style, textTransform: 'capitalize' }} title={ROLE_HELP[role]}>{role}</span>;
}

function OrganizationName({ organization, canManage, onRenamed }) {
  const [name, setName] = useState(organization.name);
  const [saving, setSaving] = useState(false);
  const dirty = name.trim() && name.trim() !== organization.name;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await orgAPI.rename(name.trim());
      onRenamed(res.data.organization);
      toast.success('Organization renamed');
    } catch (err) {
      toast.error(errorOf(err, 'Could not rename the organization'));
    } finally { setSaving(false); }
  };

  return (
    <Card title="Organization" subtitle={`Created ${fmtDate(organization.created_at)}`}>
      <form onSubmit={save} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <label className="form-label">Name</label>
          <input className="form-input" value={name} maxLength={255} disabled={!canManage} onChange={e => setName(e.target.value)} />
        </div>
        {canManage && <button className="btn btn-primary" disabled={!dirty || saving}>{saving ? 'Saving…' : 'Save'}</button>}
      </form>
    </Card>
  );
}

function InviteForm({ onInvited }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [sending, setSending] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await orgAPI.invite(email.trim(), role);
      toast.success(`Invitation sent to ${email.trim()}`);
      setEmail('');
      onInvited();
    } catch (err) {
      toast.error(errorOf(err, 'Could not send the invitation'));
    } finally { setSending(false); }
  };

  return (
    <form onSubmit={send} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div className="form-group" style={{ flex: '1 1 220px', marginBottom: 0 }}>
        <label className="form-label">Email address</label>
        <input className="form-input" type="email" placeholder="colleague@company.com" value={email} onChange={e => setEmail(e.target.value)} required />
      </div>
      <div className="form-group" style={{ width: 130, marginBottom: 0 }}>
        <label className="form-label">Role</label>
        <select className="form-select" value={role} onChange={e => setRole(e.target.value)}>
          <option value="member">Member</option>
          <option value="admin">Admin</option>
        </select>
      </div>
      <button className="btn btn-primary" disabled={sending || !email.trim()}>{sending ? 'Sending…' : 'Send invite'}</button>
    </form>
  );
}

function TransferOwnershipModal({ member, onClose, onDone }) {
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await orgAPI.transferOwnership(member.id, password);
      toast.success(`${member.name} now owns the organization`);
      onDone();
    } catch (err) {
      toast.error(errorOf(err, 'Could not transfer ownership'));
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !saving && onClose()}>
      <div className="modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><div className="modal-title">Make {member.name} the owner?</div></div>
        <div className="modal-body">
          <p style={{ fontSize: 12, color: '#4a5568', marginBottom: 14, lineHeight: 1.6 }}>
            {member.name} will own the organization, and you'll become an admin. Only the new owner can change this back.
          </p>
          <div className="form-group">
            <label className="form-label">Your current password</label>
            <input className="form-input" type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)} autoFocus />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" disabled={saving} onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving || !password} onClick={submit}>{saving ? 'Transferring…' : 'Transfer ownership'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Team() {
  const { user, setUser, checkAuth } = useStore();
  const [data, setData] = useState(null);
  const [transferTo, setTransferTo] = useState(null);
  const canManage = ['owner', 'admin'].includes(user?.org_role);
  const isOwner = user?.org_role === 'owner';

  const load = useCallback(() => {
    orgAPI.get().then(r => setData(r.data)).catch(err => toast.error(errorOf(err, 'Could not load your team')));
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, success, fallback) => {
    try { await fn(); if (success) toast.success(success); load(); }
    catch (err) { toast.error(errorOf(err, fallback)); }
  };

  const changeRole = (m, role) => act(() => orgAPI.setRole(m.id, role), `${m.name} is now ${role === 'admin' ? 'an admin' : 'a member'}`, 'Could not change the role');
  const remove = (m) => {
    if (!window.confirm(`Remove ${m.name} from ${user.organization_name}? Their sign-in will be deleted. Posts, accounts and groups they created stay.`)) return;
    act(() => orgAPI.removeMember(m.id), `${m.name} was removed`, 'Could not remove this member');
  };

  if (!data) return <div className="loading-center"><div className="spinner" /></div>;
  const { organization, members, invitations } = data;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Team</div>
          <div className="page-subtitle">Everyone here shares {organization.name}'s social accounts, groups and posts</div>
        </div>
      </div>
      <div style={{ padding: 16, maxWidth: 860 }}>
        <OrganizationName
          key={organization.name}
          organization={organization}
          canManage={canManage}
          onRenamed={(org) => { setData(d => ({ ...d, organization: org })); setUser({ ...user, organization_name: org.name }); }}
        />

        <Card title={`Members (${members.length})`} subtitle="Hover a role to see what it can do">
          <table className="data-table">
            <thead><tr><th>Name</th><th>Role</th><th>Last sign-in</th><th>Joined</th><th></th></tr></thead>
            <tbody>
              {members.map(m => {
                const isMe = m.id === user.id;
                const editable = canManage && !isMe && m.org_role !== 'owner';
                const removable = editable && (m.org_role !== 'admin' || isOwner);
                return (
                  <tr key={m.id}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Avatar user={m} size={28} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: '#1a1f2e' }}>{m.name}{isMe && <span style={{ color: '#8892a4', fontWeight: 400 }}> (you)</span>}</div>
                          <div style={{ fontSize: 11, color: '#8892a4' }}>{m.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      {editable ? (
                        <select className="form-select" style={{ width: 110, padding: '4px 8px', fontSize: 12 }} value={m.org_role} onChange={e => changeRole(m, e.target.value)}>
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                      ) : <RoleBadge role={m.org_role} />}
                    </td>
                    <td style={{ color: '#555' }}>{m.last_login_at ? fmtDate(m.last_login_at) : 'Never'}</td>
                    <td style={{ color: '#555' }}>{fmtDate(m.created_at)}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        {isOwner && !isMe && m.status === 'active' && (
                          <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setTransferTo(m)}>Make owner</button>
                        )}
                        {removable && (
                          <button className="btn btn-danger" style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => remove(m)}>Remove</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        {canManage && (
          <Card title="Invite people" subtitle="They'll get an email link to set a password and join. No approval needed.">
            <InviteForm onInvited={load} />
            {invitations.length > 0 && (
              <table className="data-table" style={{ marginTop: 16 }}>
                <thead><tr><th>Pending invitation</th><th>Role</th><th>Invited by</th><th>Expires</th><th></th></tr></thead>
                <tbody>
                  {invitations.map(i => (
                    <tr key={i.id}>
                      <td style={{ fontWeight: 500 }}>{i.email}</td>
                      <td><RoleBadge role={i.role} /></td>
                      <td style={{ color: '#555' }}>{i.invited_by_name || '—'}</td>
                      <td style={{ color: i.expired ? '#b91c1c' : '#555' }}>{i.expired ? 'Expired' : fmtDate(i.expires_at)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          <button className="btn btn-outline" style={{ fontSize: 11, padding: '4px 10px' }}
                            onClick={() => act(() => orgAPI.resendInvite(i.id), `Invitation re-sent to ${i.email}`, 'Could not resend')}>Resend</button>
                          <button className="btn btn-danger" style={{ fontSize: 11, padding: '4px 10px' }}
                            onClick={() => window.confirm(`Cancel the invitation for ${i.email}?`) && act(() => orgAPI.cancelInvite(i.id), 'Invitation cancelled', 'Could not cancel')}>Cancel</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}
      </div>

      {transferTo && (
        <TransferOwnershipModal
          member={transferTo}
          onClose={() => setTransferTo(null)}
          onDone={() => { setTransferTo(null); checkAuth(); load(); }}
        />
      )}
    </>
  );
}
